import type { AABB, Vec3 } from './math.js';

/**
 * Input button bitfield. These values are part of the (future) wire protocol —
 * keep them stable; append, never renumber.
 */
export const Button = {
  Forward: 1 << 0,
  Back: 1 << 1,
  Left: 1 << 2,
  Right: 1 << 3,
  Jump: 1 << 4,
  Crouch: 1 << 5,
  Sprint: 1 << 6,
  Ads: 1 << 7,
  Fire: 1 << 8,
  Reload: 1 << 9,
  Use: 1 << 10,
  Melee: 1 << 11,
} as const;

export type ButtonName = keyof typeof Button;

export const hasButton = (buttons: number, b: number): boolean =>
  (buttons & b) !== 0;

/**
 * One fixed-step input command. `yaw`/`pitch` are absolute view angles in
 * radians (the server trusts movement intent but re-derives outcomes). One
 * command is consumed per simulation tick.
 */
export interface InputCommand {
  /** Monotonic sequence number, used later for reconciliation. */
  seq: number;
  buttons: number;
  yaw: number;
  pitch: number;
}

/** Authoritative per-player movement state advanced by {@link stepMovement}. */
export interface MovementState {
  /** Feet position (y = 0 means standing on the ground plane). */
  pos: Vec3;
  vel: Vec3;
  yaw: number;
  pitch: number;
  onGround: boolean;
  crouching: boolean;
}

/** Tunable movement parameters. All speeds in units/sec, times in seconds. */
export interface MovementConfig {
  walkSpeed: number;
  sprintMultiplier: number;
  crouchMultiplier: number;
  /** Ground acceleration toward target velocity (units/sec^2). */
  accel: number;
  /** Ground friction deceleration when there is no input (units/sec^2). */
  friction: number;
  /** Reduced control while airborne (fraction of `accel`). */
  airControl: number;
  gravity: number;
  jumpSpeed: number;
  /** Player AABB horizontal half-extent (radius). */
  radius: number;
  standHeight: number;
  crouchHeight: number;
  /** Eye offset below the top of the capsule. */
  eyeDrop: number;
  /** Max ledge height the player walks up without jumping (stairs). */
  stepHeight: number;
}

export const DEFAULT_MOVEMENT: MovementConfig = {
  walkSpeed: 8.5,
  sprintMultiplier: 1.75,
  crouchMultiplier: 0.5,
  accel: 80,
  friction: 60,
  airControl: 0.3,
  gravity: 22,
  jumpSpeed: 7.8,
  radius: 0.4,
  standHeight: 1.8,
  crouchHeight: 1.2,
  eyeDrop: 0.2,
  stepHeight: 0.35,
};

/** Static, immutable world the simulation collides against. */
export interface WorldStatics {
  colliders: AABB[];
  /** Soft play-area bounds (clamps feet position on x/z). */
  bounds: { minX: number; maxX: number; minZ: number; maxZ: number };
  groundY: number;
}
